import type { ReactNode } from "react";
import { useT } from "@/contexts/I18nContext";

/**
 * A table.
 *
 * A real `<table>`, not a grid of divs: the people using this read down a
 * column of thirty names, and a screen reader has to be able to say which
 * column it is in.
 */

export interface Column<TRow> {
  key: string;
  /** i18n key for the header. */
  headerKey: string;
  render: (row: TRow) => ReactNode;
  /** Narrow columns — a status dot, an action menu. */
  width?: string;
  align?: "left" | "right";
}

export function Table<TRow>({
  columns,
  rows,
  rowKey,
  empty,
  caption,
}: {
  columns: readonly Column<TRow>[];
  rows: readonly TRow[];
  rowKey: (row: TRow) => string;
  /** Rendered instead of the body when there are no rows. */
  empty?: ReactNode;
  /** i18n key. Visually hidden; describes the table to a screen reader. */
  caption?: string;
}) {
  const t = useT();

  if (rows.length === 0 && empty) return <>{empty}</>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[14px]">
        {caption ? <caption className="sr-only">{t(caption)}</caption> : null}
        <thead>
          <tr className="border-b border-line bg-surface-muted">
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                style={column.width ? { width: column.width } : undefined}
                className={`h-9 px-3 text-[13px] font-medium text-ink-muted ${
                  column.align === "right" ? "text-right" : "text-left"
                }`}
              >
                {t(column.headerKey)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} className="border-b border-line last:border-0">
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={`h-10 px-3 py-2.5 align-middle ${
                    column.align === "right" ? "text-right" : "text-left"
                  }`}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
